import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { mockPartners } from '../../../mocks/partners';
import { mockReplies } from '../../../mocks/replies';
import { mockPosts } from '../../../mocks/posts';
import RelatedLinks from '../../../components/feature/RelatedLinks';

export default function PartnerProfilePage() {
  const { id } = useParams<{ id: string }>();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const partner = mockPartners.find((p) => p.id === id);

  // パートナーの活動履歴を取得
  const partnerReplies = mockReplies.filter(
    (reply) =>
      reply.profiles?.nickname === partner?.nickname &&
      reply.profiles?.partner_role === partner?.partner_role
  );

  // 返信に対応する投稿情報を取得
  const activityHistory = partnerReplies.map((reply) => {
    const post = mockPosts.find((p) => p.id === reply.post_id);
    return {
      ...reply,
      postTitle: post?.title || '投稿',
      postCategory: post?.category || 'つぶやき・モヤモヤ',
    };
  }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  if (!partner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-user-unfollow-line text-6xl text-gray-300 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">パートナーが見つかりません</h2>
          <Link to="/partners" className="text-teal-600 hover:underline cursor-pointer">
            パートナー一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case '看護師':
        return 'from-teal-500 to-cyan-500';
      case '保育士':
        return 'from-pink-500 to-rose-500';
      case '先輩ママ':
        return 'from-purple-500 to-pink-500';
      case '理学療法士':
        return 'from-blue-500 to-indigo-500';
      case '社会福祉士':
        return 'from-amber-500 to-orange-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case '看護師':
        return 'ri-nurse-line';
      case '保育士':
        return 'ri-parent-line';
      case '先輩ママ':
        return 'ri-heart-line';
      case '理学療法士':
        return 'ri-run-line';
      case '社会福祉士':
        return 'ri-hand-heart-line';
      default:
        return 'ri-user-line';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-teal-50">
      {/* ナビゲーション */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-purple-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-ship-line text-white text-xl"></i>
              </div>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
                スマイル・ポート
              </span>
            </Link>

            {/* デスクトップメニュー */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                ホーム
              </Link>
              <Link to="/board" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                みんなの休憩室
              </Link>
              <Link to="/manual" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                お守りマニュアル
              </Link>
              <Link to="/partners" className="text-teal-600 font-medium whitespace-nowrap cursor-pointer">
                パートナー一覧
              </Link>
            </div>

            {/* モバイルハンバーガーメニュー */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-gray-900"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* モバイルメニュー */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white shadow-lg rounded-b-xl">
              <div className="px-4 py-3 space-y-3">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">
                  ホーム
                </Link>
                <Link to="/board" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">
                  みんなの休憩室
                </Link>
                <Link to="/manual" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">
                  お守りマニュアル
                </Link>
                <Link to="/partners" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-teal-600 cursor-pointer">
                  パートナー一覧
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 戻るボタン */}
        <Link
          to="/partners"
          className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 mb-6 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span className="text-sm font-medium">パートナー一覧に戻る</span>
        </Link>

        {/* プロフィールカード */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8">
          {/* ヘッダー */}
          <div className={`bg-gradient-to-r ${getRoleColor(partner.partner_role)} p-8 text-white`}>
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6">
              <img
                src={partner.avatar_url}
                alt={partner.nickname}
                className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
              />
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start space-x-3 mb-2">
                  <h1 className="text-3xl font-bold">{partner.nickname}</h1>
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <i className={`${getRoleIcon(partner.partner_role)} text-2xl`}></i>
                  </div>
                </div>
                <p className="text-xl mb-2">{partner.partner_role}</p>
                <div className="flex items-center justify-center md:justify-start space-x-2 text-white/90">
                  <i className="ri-map-pin-line"></i>
                  <span>{partner.area}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 統計情報 */}
          <div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-gray-200">
            <div className="p-6 text-center">
              <div className="text-3xl font-bold text-teal-600 mb-1">{partner.reply_count}</div>
              <div className="text-sm text-gray-600">回答数</div>
            </div>
            <div className="p-6 text-center">
              <div className="text-3xl font-bold text-rose-600 mb-1">{partner.thanks_count}</div>
              <div className="text-sm text-gray-600">感謝された数</div>
            </div>
            <div className="p-6 text-center">
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {Math.floor((new Date().getTime() - new Date(partner.joined_at).getTime()) / (1000 * 60 * 60 * 24 * 30))}
              </div>
              <div className="text-sm text-gray-600">活動月数</div>
            </div>
          </div>

          {/* 自己紹介 */}
          <div className="p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
              <i className="ri-user-heart-line text-purple-600"></i>
              <span>自己紹介</span>
            </h2>
            <p className="text-gray-700 leading-relaxed">{partner.bio}</p>
          </div>

          {/* 専門分野 */}
          <div className="px-8 pb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
              <i className="ri-star-line text-amber-600"></i>
              <span>専門分野</span>
            </h2>
            <div className="flex flex-wrap gap-2">
              {partner.expertise.map((skill, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-700 rounded-full text-sm font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* 勤務先 */}
          {partner.workplace && (
            <div className="px-8 pb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                <i className="ri-building-line text-indigo-600"></i>
                <span>勤務先</span>
              </h2>
              <a
                href={partner.workplace.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-gradient-to-br from-gray-50 to-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <i className="ri-building-line text-indigo-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 group-hover:text-teal-600 transition-colors">
                        {partner.workplace.name}
                      </p>
                      <p className="text-xs text-gray-500">公式サイトを見る</p>
                    </div>
                  </div>
                  <i className="ri-external-link-line text-gray-400 group-hover:text-teal-600 transition-colors"></i>
                </div>
              </a>
            </div>
          )}

          {/* SNSリンク */}
          {(partner.sns.instagram || partner.sns.twitter || partner.sns.line) && (
            <div className="px-8 pb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                <i className="ri-share-line text-pink-600"></i>
                <span>SNS</span>
              </h2>
              <div className="flex flex-wrap gap-3">
                {partner.sns.instagram && (
                  <a
                    href={partner.sns.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl hover:shadow-lg transition-all cursor-pointer"
                  >
                    <i className="ri-instagram-line text-xl"></i>
                    <span className="font-medium">Instagram</span>
                  </a>
                )}
                {partner.sns.twitter && (
                  <a
                    href={partner.sns.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-sky-500 to-blue-500 text-white rounded-xl hover:shadow-lg transition-all cursor-pointer"
                  >
                    <i className="ri-twitter-x-line text-xl"></i>
                    <span className="font-medium">X (Twitter)</span>
                  </a>
                )}
                {partner.sns.line && (
                  <a
                    href={partner.sns.line}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:shadow-lg transition-all cursor-pointer"
                  >
                    <i className="ri-line-line text-xl"></i>
                    <span className="font-medium">LINE</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* 活動履歴 */}
          <div className="px-8 pb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
              <i className="ri-history-line text-teal-600"></i>
              <span>活動履歴</span>
            </h2>
            {activityHistory.length > 0 ? (
              <div className="space-y-4">
                {activityHistory.slice(0, 5).map((activity) => (
                  <Link
                    key={activity.id}
                    to={`/board/post/${activity.post_id}`}
                    className="block bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 hover:shadow-md transition-all cursor-pointer group"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-chat-quote-line text-teal-600"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                            {activity.postCategory}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(activity.created_at).toLocaleDateString('ja-JP')}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 group-hover:text-teal-600 transition-colors mb-1 truncate">
                          {activity.postTitle}
                        </p>
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {activity.content}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-teal-600 transition-colors"></i>
                    </div>
                  </Link>
                ))}
                {activityHistory.length > 5 && (
                  <p className="text-center text-sm text-gray-500">
                    他 {activityHistory.length - 5} 件の活動があります
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-xl">
                <i className="ri-chat-off-line text-4xl text-gray-300 mb-2"></i>
                <p className="text-gray-500">まだ活動履歴がありません</p>
              </div>
            )}
          </div>
        </div>

        {/* CTAカード */}
        <div className="bg-gradient-to-br from-teal-100 to-cyan-100 rounded-3xl p-8 text-center">
          <i className="ri-chat-heart-line text-5xl text-teal-600 mb-4"></i>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            {partner.nickname}さんに相談してみませんか？
          </h3>
          <p className="text-gray-700 mb-6">
            みんなの休憩室で投稿する際に「パートナー回答希望」をつけると、<br />
            {partner.nickname}さんからアドバイスがもらえるかもしれません。
          </p>
          <Link
            to="/board"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-3-line text-xl"></i>
            <span>みんなの休憩室へ</span>
          </Link>
        </div>
      </div>

      {/* 関連リンク */}
      <RelatedLinks />

      {/* フッター */}
      <footer className="bg-gradient-to-r from-teal-700 to-teal-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-ship-line text-white text-xl"></i>
                </div>
                <span className="text-xl font-bold">みやぎスマイル・ポート</span>
              </div>
              <p className="text-teal-100 text-sm">医療的ケア児と家族の港</p>
            </div>
            <div>
              <h4 className="font-bold mb-3">リンク</h4>
              <ul className="space-y-2 text-sm text-teal-100">
                <li>
                  <Link to="/" className="hover:text-white transition-colors cursor-pointer">
                    ホーム
                  </Link>
                </li>
                <li>
                  <Link to="/board" className="hover:text-white transition-colors cursor-pointer">
                    みんなの休憩室
                  </Link>
                </li>
                <li>
                  <Link to="/manual" className="hover:text-white transition-colors cursor-pointer">
                    お守りマニュアル
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3">お問い合わせ</h4>
              <p className="text-sm text-teal-100">support@smile-port.jp</p>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-8 text-center text-sm text-teal-100">
            <p>
              &copy; 2025 みやぎスマイル・ポート. All rights reserved. |{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
